import { useRef, type ComponentProps, type ReactNode } from 'react';
import { Button, type ButtonSize, type ButtonVariant } from './Button';

/**
 * A button that opens the file picker; the file input stays hidden inside ui/. The input is cleared after each
 * choice, so choosing the same file twice reports it twice.
 */
export function FileButton({
  accept,
  multiple,
  onFiles,
  children,
  variant,
  size,
  disabled,
  className,
}: {
  accept?: string;
  multiple?: boolean;
  onFiles: (files: File[]) => void;
  children: ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  disabled?: boolean;
  className?: string;
}) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <>
      <Button variant={variant} size={size} disabled={disabled} className={className} onClick={() => input.current?.click()}>
        {children}
      </Button>
      <FilePicker ref={input} accept={accept} multiple={multiple} onFiles={onFiles} />
    </>
  );
}

/**
 * The hidden file input of a picker opened from elsewhere (a menu item, the camera, "Replace image"): the caller clicks
 * it through its `ref`. Cleared after each choice, so choosing the same file twice reports it twice.
 */
export function FilePicker({ onFiles, ...rest }: Omit<ComponentProps<'input'>, 'type' | 'hidden' | 'onChange'> & { onFiles: (files: File[]) => void }) {
  return (
    <input
      type="file"
      hidden
      tabIndex={-1}
      onChange={(e) => {
        const files = [...(e.currentTarget.files ?? [])];
        e.currentTarget.value = '';
        if (files.length) onFiles(files);
      }}
      {...rest}
    />
  );
}
